<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { useWorkflowInputKeys } from "@/queries/workflows";
import { useRunWithReadinessConfirm, RunCancelledError } from "@/composables/useRunWithReadinessConfirm";
import RunReadinessConfirmDialog from "@/components/RunReadinessConfirmDialog.vue";
import { ApiError } from "@/helpers/api-error";

interface Props {
  show: boolean;
  projectId: string;
  workflowId: string | null;
  workflowName?: string;
  /** Seed the input editors with these values (e.g. re-running a past run with its original inputs). */
  initialInputs?: Record<string, unknown>;
  /**
   * When set, the modal does NOT navigate to the project Run History page after starting the run —
   * it emits `ran` with the new run id and leaves navigation to the caller. Used by the builder,
   * which wants to keep the user on the canvas and load the new run in history mode instead.
   */
  stayOnSuccess?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "ran", runId: string): void;
}>();

const router = useRouter();
const queryClient = useQueryClient();

const projectIdRef = computed(() => props.projectId);
const workflowIdRef = computed(() => props.workflowId);

const { inputKeys, inputKeysLoading, inputKeysError } = useWorkflowInputKeys(
  projectIdRef,
  workflowIdRef
);

// Canonical run path — same readiness gate ("Run anyway" confirm) the Workflows tab uses, plus the
// per-run inputs collected below. The modal hosts the confirm dialog itself.
const { run, confirmOpen, issues, advisories, submitting, runAnyway, cancel } = useRunWithReadinessConfirm();
const runError = ref<string | null>(null);

// Per-key draft state — each input value is edited as JSON text (mirrors DataStoresTab). On run we
// parse each; parse errors surface inline and block the run.
const drafts = ref<Record<string, string>>({});
const parseErrors = ref<Record<string, string | null>>({});

const keys = computed(() => inputKeys.value?.keys ?? []);

// Render a stored input value as the JSON text the textarea edits. Strings the user would have typed
// as bare JSON (e.g. `"value"`) round-trip through JSON.stringify; objects pretty-print.
function toDraft(value: unknown): string {
  if (value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
}

// Reset drafts when the modal opens or the target workflow changes, so no value leaks from a
// previously-run workflow into this one (input key names commonly collide across workflows). On a
// re-run, `initialInputs` seeds the editors with the original run's values.
watch(
  () => [props.show, props.workflowId] as const,
  ([show]) => {
    if (!show) return;
    const seed: Record<string, string> = {};
    for (const [key, value] of Object.entries(props.initialInputs ?? {})) {
      seed[key] = toDraft(value);
    }
    drafts.value = seed;
    parseErrors.value = {};
    runError.value = null;
  },
  { immediate: true }
);

// As the declared keys resolve, ensure each has a draft. `?? ""` keeps anything already seeded (from
// initialInputs) or typed within the CURRENT open session (drafts is reset on open / workflow change).
watch(
  () => keys.value,
  (currentKeys) => {
    if (!props.show) return;
    const fresh: Record<string, string> = {};
    for (const key of currentKeys) fresh[key] = drafts.value[key] ?? "";
    drafts.value = fresh;
  },
  { immediate: true }
);

function close() {
  if (submitting.value) return;
  emit("update:show", false);
}

async function confirmRun() {
  if (!props.workflowId) return;
  runError.value = null;

  // Parse every non-empty draft; empty means "not supplied" and is simply omitted.
  const inputs: Record<string, unknown> = {};
  const errors: Record<string, string | null> = {};
  let hasError = false;
  for (const key of keys.value) {
    const raw = (drafts.value[key] ?? "").trim();
    if (raw === "") continue;
    try {
      inputs[key] = JSON.parse(raw);
      errors[key] = null;
    } catch (e) {
      errors[key] = (e as Error).message || "Invalid JSON";
      hasError = true;
    }
  }
  parseErrors.value = errors;
  if (hasError) return;

  try {
    // Safe-by-default readiness gate (ADR-033): blocking config gaps open the pre-run confirm below;
    // "Run anyway" overrides. Inputs ride along and survive the override retry.
    const runId = await run({
      projectId: props.projectId,
      workflowId: props.workflowId,
      inputs: Object.keys(inputs).length > 0 ? inputs : undefined,
    });
    // Refresh the project's run-history list so the run we just started shows up. useStartWorkflowRun
    // invalidates the builder's "workflow-runs" key; the project History tab reads a separate
    // "project-runs" query, so invalidate it here too (harmless when run from the Workflows tab).
    queryClient.invalidateQueries({ queryKey: ["project-runs", props.projectId] });
    emit("update:show", false);
    if (props.stayOnSuccess) {
      // Caller (the builder) handles where to go — it keeps the user on the canvas and loads the
      // new run in history mode rather than navigating away to the project Run History page.
      emit("ran", runId);
      return;
    }
    // Land on the project's Run History tab for the run we just started (not the builder editor).
    router.push({
      name: "project-history-run",
      params: {
        projectid: props.projectId,
        workflowid: props.workflowId,
        runid: runId,
      },
    });
  } catch (e) {
    if (e instanceof RunCancelledError) return; // user declined at the readiness gate
    runError.value = e instanceof ApiError ? e.userMessage : "The run could not be started.";
  }
}
</script>

<template>
  <div
    v-if="show"
    class="modal fade show d-block"
    tabindex="-1"
    @click="close"
  >
    <div class="modal-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title mb-0">Run workflow</h2>
          <button
            type="button"
            class="btn-close"
            :disabled="submitting"
            @click="close"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            Run
            <strong>{{ workflowName ?? "this workflow" }}</strong
            ><span v-if="keys.length > 0"> with the inputs below</span>.
          </p>

          <div v-if="inputKeysLoading" class="text-muted small">
            <span class="spinner-border spinner-border-sm me-2" role="status"></span>
            Loading inputs…
          </div>

          <div v-else-if="inputKeysError" class="alert alert-warning mb-0">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Couldn't load this workflow's inputs.
          </div>

          <p v-else-if="keys.length === 0" class="text-muted small mb-0">
            This workflow declares no inputs — it'll run with its configured values.
          </p>

          <div v-else class="d-flex flex-column gap-3">
            <div v-for="key in keys" :key="key">
              <label class="form-label small fw-semibold mb-1">{{ key }}</label>
              <textarea
                v-model="drafts[key]"
                class="form-control font-monospace small"
                rows="3"
                spellcheck="false"
                placeholder='e.g. "value" or { "id": 1 }'
              ></textarea>
              <div v-if="parseErrors[key]" class="text-danger small mt-1">
                <i class="bi bi-exclamation-triangle me-1"></i>
                {{ parseErrors[key] }}
              </div>
            </div>
            <p class="text-muted small mb-0">
              Each value is parsed as JSON. Leave a field blank to omit it.
            </p>
          </div>

          <div v-if="runError" class="alert alert-danger mt-3 mb-0">
            <i class="bi bi-exclamation-circle me-2"></i>
            Failed to start the run. {{ runError }}
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-secondary-cta"
            :disabled="submitting"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            :disabled="submitting || inputKeysLoading"
            @click="confirmRun"
          >
            <span
              v-if="submitting"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="bi bi-play me-1"></i>
            Run
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Safe-by-default readiness gate (ADR-033) — same confirm the Workflows tab uses. -->
  <RunReadinessConfirmDialog
    :open="confirmOpen"
    :issues="issues"
    :advisories="advisories"
    :submitting="submitting"
    :project-id="projectIdRef"
    @run-anyway="runAnyway"
    @cancel="cancel"
  />
</template>
